import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLang, useTextDomainContext, useUser } from "@/app/providers";
import {
  GENERATE_ENERGY_STATEMENT_FILE,
  usePageTitle,
  useGetGroups,
  useEnergyData,
} from "@/hooks";

import {
  FilterBar1,
  AreaChartBox,
  LineChartBox,
  BarChartBox,
  TableView1,
  ToolbarDropdown,
} from "@/components";
import {
  exportableStatements,
  formatDateForAPI,
  segmentList,
  toLocaleNumber,
  formatDateYear,
  getPreviousYearDate,
  toBenchmarkDataEnergy,
} from "@/utils";

// Control whether the bar chart is shown under the line chart
import { useTheme, Box, Typography } from "@mui/material";
import { TfiImport } from "react-icons/tfi";
import { enqueueSnackbar } from "notistack";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { queries } from "@/graphql";
import { useNavigate } from "react-router";
import type { GridColDef, GridRowClassNameParams } from "@mui/x-data-grid";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FunctionsIcon from "@mui/icons-material/Functions";

export const EnergyConsumption = () => {
  type SelectedValuesPayload = {
    building?: { value?: string } | string;
    meter?: { value?: string } | string;
    energyType?: { value?: string } | string;
    date?: { startDate?: Date; endDate?: Date };
    segment?: "year" | "month" | "day";
    country?: string;
    unit?: { value?: string } | string;
    day?: { value?: string } | string;
  };

  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const navigate = useNavigate();
  const { user } = useUser();
  const isSuperAdmin = user?.roles?.includes("super-admin");
  // const energyTypes = useEnergyTypeArray();

  // Set page title
  usePageTitle(gettext("Energy Consumption"));

  // Fetch energy-chart-type settings with user -> tenant -> global fallback
  const { data: userSettingData } = useQuery(queries.userSettings, {
    variables: { key: "energy-chart-type" },
  });

  const { data: tenantSettingData } = useQuery(queries.tenantSettings, {
    variables: { key: "energy-chart-type" },
  });

  const { data: globalSettingData } = useQuery(queries.getSettings, {
    variables: { key: "energy-chart-type" },
  });

  const activeChartType = useMemo(() => {
    const extractVal = (raw: any): string | null => {
      if (!raw) return null;
      let parsed: any = raw;
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "userSettings" in parsed
      ) {
        parsed = parsed.userSettings;
      }
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "userSettings" in parsed
      ) {
        parsed = parsed.userSettings;
      }
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "tenantSettings" in parsed
      ) {
        parsed = parsed.tenantSettings;
      }
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "settings" in parsed
      ) {
        parsed = parsed.settings;
      }
      return typeof parsed === "string" ? parsed : null;
    };

    // Priority 1: User Setting
    const userVal = extractVal(userSettingData);
    if (userVal && userVal !== "Default") {
      return userVal;
    }

    // Priority 2: Tenant Setting
    const tenantVal = extractVal(tenantSettingData);
    if (tenantVal && tenantVal !== "Default") {
      return tenantVal;
    }

    // Priority 3: System (Global) Setting
    const globalVal = extractVal(globalSettingData);
    if (globalVal && globalVal !== "Default") {
      return globalVal;
    }

    return "Bar Chart";
  }, [userSettingData, tenantSettingData, globalSettingData]);

  const showAreaChart = activeChartType === "Area Chart";
  const showLineChart = activeChartType === "Line Chart";
  const showBarChart = activeChartType === "Bar Chart";

  const segments = segmentList();
  let {
    currentTeam: customer,
    currentSubsidiary: group,
    currentTeamId,
    currentSubsidiaryId,
  } = user || {};
  const theme = useTheme();
  const locale = useLang();

  if (!group) {
    group = "All";
  }

  //#region useState
  const [building, setBuilding] = useState("all");
  const [meter, setMeter] = useState("all");
  const [energyType, setEnergyType] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setMonth(0, 1); // January 1st of current year
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date.toISOString();
  });
  const [shortSegment, setShortSegment] = useState("m");
  // const [chartData, setChartData] = useState([]);
  // const [tableData, setTableData] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [country, setCountry] = useState(null);
  const [unit, setUnit] = useState("m3");
  const [day, setDay] = useState("none");
  //#endregion useState

  //#region useMemo
  // const selectedEnergyType = useMemo(() => {
  //   let selected = energyTypes.find(type => type.value === energyType);
  //   if (selected.label) {
  //     selected = selected.label;
  //   } else {
  //     selected = "";
  //   }
  //   return selected;
  // }, [energyType, energyTypes]);
  //#endregion useMemo

  //#region Data
  const thisYearFilter = {
    customer,
    group,
    address: building,
    meteringPointId: meter,
    meterType: energyType,
    fromDate: formatDateForAPI(fromDate),
    toDate: formatDateForAPI(toDate),
    resolution: shortSegment,
    country,
  };

  const previousYearFilter = {
    customer,
    group,
    address: building,
    meteringPointId: meter,
    meterType: energyType,
    fromDate: getPreviousYearDate(thisYearFilter.fromDate),
    toDate: getPreviousYearDate(thisYearFilter.toDate),
    resolution: shortSegment,
    country,
  };

  const twoYearsAgoFilter = {
    customer,
    group,
    address: building,
    meteringPointId: meter,
    meterType: energyType,
    fromDate: getPreviousYearDate(previousYearFilter.fromDate),
    toDate: getPreviousYearDate(previousYearFilter.toDate),
    resolution: shortSegment,
    country,
  };

  const {
    data: metersDataThisYear,
    total: totalThisYear,
    loading: loadingThisYear,
  } = useEnergyData(thisYearFilter);

  const {
    data: metersDataPreviousYear,
    total: totalPreviousYear,
    loading: loadingPreviousYear,
  } = useEnergyData(previousYearFilter);

  const {
    data: metersDataTwoYearsAgo,
    total: totalTwoYearsAgo,
    loading: loadingTwoYearsAgo,
  } = useEnergyData(twoYearsAgoFilter);

  const [addExcelInQueue] = useLazyQuery(GENERATE_ENERGY_STATEMENT_FILE);

  const { subsidiary } = useGetGroups({
    customerId: currentTeamId,
    customerName: customer,
  });

  // const [addExcelInQueue] = useLazyQuery(GENERATE_ENERGY_STATEMENT_FILE, {
  //   variables: {
  //     meterType: energyType,
  //     customer: customer,
  //     group: group,
  //     year: new Date(fromDate).getFullYear(),
  //     isClimate: false,
  //   },
  // });
  //#endregion Data

  //region React Hooks (useEffect)

  useEffect(() => {
    if (!customer) {
      // window.location.href = window.location.origin + "/error/select-company";
      navigate(`/user-account/profile`);
    }
  }, [customer, navigate]);

  const tableData = useMemo(() => {
    return toBenchmarkDataEnergy(
      shortSegment,
      [
        {
          data: metersDataThisYear,
          total: totalThisYear,
        },
        {
          data: metersDataPreviousYear,
          total: totalPreviousYear,
        },
        {
          data: metersDataTwoYearsAgo,
          total: totalTwoYearsAgo,
        },
      ],
      gettext,
      true,
    );
  }, [
    shortSegment,
    metersDataThisYear,
    totalThisYear,
    metersDataPreviousYear,
    totalPreviousYear,
    metersDataTwoYearsAgo,
    totalTwoYearsAgo,
    gettext,
  ]);

  const chartData = useMemo(() => {
    return toBenchmarkDataEnergy(
      shortSegment,
      [
        {
          data: metersDataThisYear,
          total: totalThisYear,
        },
        {
          data: metersDataPreviousYear,
          total: totalPreviousYear,
        },
        {
          data: metersDataTwoYearsAgo,
          total: totalTwoYearsAgo,
        },
      ],
      gettext,
      false,
    );
  }, [
    shortSegment,
    metersDataThisYear,
    totalThisYear,
    metersDataPreviousYear,
    totalPreviousYear,
    metersDataTwoYearsAgo,
    totalTwoYearsAgo,
  ]);

  // useEffect(() => {
  //   const calculatedTableData = toBenchmarkDataEnergy(
  //     shortSegment,
  //     [
  //       {
  //         data: metersDataThisYear,
  //         total: totalThisYear,
  //       },
  //       {
  //         data: metersDataPreviousYear,
  //         total: totalPreviousYear,
  //       },
  //     ],
  //     gettext,
  //     true,
  //   );
  //
  //   const calculatedChartData = toBenchmarkDataEnergy(
  //     shortSegment,
  //     [
  //       {
  //         data: metersDataThisYear,
  //         total: totalThisYear,
  //       },
  //       {
  //         data: metersDataPreviousYear,
  //         total: totalPreviousYear,
  //       },
  //     ],
  //     false,
  //   );
  //
  //   if (!isEqual(tableData, calculatedTableData)) {
  //     setTableData(calculatedTableData);
  //   }
  //
  //   if (!isEqual(chartData, calculatedChartData)) {
  //     setChartData(calculatedChartData);
  //   }
  // }, [
  //   chartData,
  //   gettext,
  //   metersDataPreviousYear,
  //   metersDataThisYear,
  //   shortSegment,
  //   tableData,
  //   totalPreviousYear,
  //   totalThisYear,
  // ]);

  useEffect(() => {
    const fetchData = async () => {
      const customerName = group.toLowerCase() === "all" ? customer : group;
      let fileNamePostfix = group.toLowerCase() === "all" ? "-all" : "";
      if (subsidiary.length < 2) {
        fileNamePostfix = "-all";
      }

      const res = [];

      const res1 = await exportableStatements(
        "energy-statement",
        customerName,
        fileNamePostfix,
        energyType,
        fromDate,
        gettext,
        unit,
        day,
      );

      if (isSuperAdmin) {
        // const res2 = await exportableStatementsTest(
        //   "energy-statement",
        //   customerName,
        //   fileNamePostfix,
        //   energyType,
        //   fromDate,
        //   gettext,
        // );

        // res.push(...res1, ...res2);
        res.push(...res1);

        const obj = {
          label: gettext("Generate Energy Report"),
          key: "generate-report",
        };

        res.push(obj);
      } else {
        res.push(...res1);
      }

      setExportData(res);
    };
    fetchData().then();
  }, [
    customer,
    energyType,
    fromDate,
    gettext,
    group,
    isSuperAdmin,
    unit,
    day,
    subsidiary.length,
  ]);
  //endregion React Hooks (useEffect)

  //region For Table

  const isGas = useMemo(() => {
    const t = (
      typeof energyType === "string"
        ? energyType
        : (energyType as any)?.value || (energyType as any)?.label || ""
    )
      .toLowerCase()
      .trim();
    return t === "gas" || t === "natural gas";
  }, [energyType]);

  const isDisplayM3 = useMemo(() => {
    if (!isGas) return false;
    return (unit || "").toLowerCase() === "m3";
  }, [isGas, unit]);

  const columns = useMemo((): GridColDef[] => {
    const twoYearsAgoField = formatDateYear(twoYearsAgoFilter.fromDate);
    const previousYearField = formatDateYear(previousYearFilter.fromDate);
    const currentYearField = formatDateYear(thisYearFilter.fromDate);

    const getPrevValue = (row: any, fieldName: string) => {
      if (row[fieldName] !== undefined && row[fieldName] !== null) {
        return row[fieldName];
      }
      const isM3 = fieldName.endsWith("_m3");
      const baseYearStr = isM3 ? fieldName.replace("_m3", "") : fieldName;
      const yearNum = Number(baseYearStr);
      if (!isNaN(yearNum)) {
        const fallbackYear = String(yearNum - 1);
        const fallbackField = isM3 ? `${fallbackYear}_m3` : fallbackYear;
        if (row[fallbackField] !== undefined && row[fallbackField] !== null) {
          return row[fallbackField];
        }
      }
      return undefined;
    };

    const renderYearCell = (prevYearField?: string) => (params: any) => {
      const isTotalRow = params.row.id === "Total";
      const fieldName = params.field;
      const row = params.row;
      const valKwh = params.value;
      const valM3 =
        row[`${fieldName}_m3`] ??
        (valKwh !== undefined && valKwh !== null
          ? Number(valKwh) / 12.157
          : undefined);

      const val = isDisplayM3 ? valM3 : valKwh;
      const unitLabel = isDisplayM3 ? "m³" : "kWh";
      const formattedVal = toLocaleNumber(locale, val ?? 0, 2);

      let diffInfo: {
        diffStr: string;
        pctStr: string | null;
        isHigher: boolean;
        isLower: boolean;
        color: string;
        bgColor: string;
      } | null = null;

      if (prevYearField && val !== undefined && val !== null) {
        const prevVal = isDisplayM3
          ? (getPrevValue(row, `${prevYearField}_m3`) ??
            (getPrevValue(row, prevYearField) !== undefined
              ? Number(getPrevValue(row, prevYearField)) / 12.157
              : undefined))
          : getPrevValue(row, prevYearField);

        if (prevVal !== undefined && prevVal !== null) {
          const numVal = Number(val);
          const numPrev = Number(prevVal);

          if (!isNaN(numVal) && !isNaN(numPrev)) {
            const diff = numVal - numPrev;
            const pct = numPrev !== 0 ? (diff / numPrev) * 100 : null;

            const formattedDiff = `${diff > 0 ? "+" : ""}${toLocaleNumber(locale, diff, 2)}`;
            const formattedPct =
              pct !== null
                ? `${pct > 0 ? "+" : ""}${toLocaleNumber(locale, pct, 1)}%`
                : null;

            const isHigher = diff > 0;
            const isLower = diff < 0;

            diffInfo = {
              diffStr: formattedDiff,
              pctStr: formattedPct,
              isHigher,
              isLower,
              color: isHigher
                ? theme.palette.error.main
                : isLower
                  ? theme.palette.success.main
                  : theme.palette.text.secondary,
              bgColor: isHigher
                ? theme.palette.mode === "dark"
                  ? "rgba(244, 67, 54, 0.15)"
                  : "#ffebee"
                : isLower
                  ? theme.palette.mode === "dark"
                    ? "rgba(76, 175, 80, 0.15)"
                    : "#e8f5e9"
                  : theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "#f5f5f5",
            };
          }
        }
      }

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            width: "100%",
            py: isTotalRow ? 1 : 0.75,
            px: 0.5,
          }}
        >
          <Box
            sx={{
              width: "100%",
              textAlign: "right",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              minHeight: "42px",
            }}
          >
            <Box
              sx={{
                fontWeight: isTotalRow ? 700 : 600,
                fontSize: isTotalRow ? "0.9rem" : "0.85rem",
                color: isTotalRow ? "primary.main" : "text.primary",
                lineHeight: 1.2,
              }}
            >
              {formattedVal}{" "}
              <Typography
                component="span"
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: isTotalRow ? 600 : 500,
                  color: isTotalRow ? "primary.main" : "text.secondary",
                }}
              >
                {unitLabel}
              </Typography>
            </Box>

            {diffInfo ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "3px",
                  color: diffInfo.color,
                  backgroundColor: diffInfo.bgColor,
                  fontWeight: isTotalRow ? 700 : 600,
                  fontSize: "0.72rem",
                  mt: 0.25,
                  px: 0.75,
                  py: 0.2,
                  borderRadius: "4px",
                  border: isTotalRow ? `1px solid ${diffInfo.color}` : "none",
                }}
              >
                {diffInfo.isHigher && (
                  <ArrowUpwardIcon sx={{ fontSize: "13px" }} />
                )}
                {diffInfo.isLower && (
                  <ArrowDownwardIcon sx={{ fontSize: "13px" }} />
                )}
                <span>
                  {diffInfo.diffStr} {unitLabel}
                </span>
                {diffInfo.pctStr && <span>({diffInfo.pctStr})</span>}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "inline-flex",
                  visibility: "hidden",
                  fontSize: "0.72rem",
                  mt: 0.25,
                  py: 0.2,
                }}
              >
                <span>-</span>
              </Box>
            )}
          </Box>
        </Box>
      );
    };

    return [
      {
        field: "ts",
        headerName: gettext("Date"),
        flex: 1,
        renderCell: (params: any) => {
          if (params.row.id === "Total") {
            return (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  px: 1.2,
                  py: 0.4,
                  borderRadius: "6px",
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                }}
              >
                <FunctionsIcon sx={{ fontSize: "15px" }} />
                <span>{params.value || gettext("SUM")}</span>
              </Box>
            );
          }
          return <span style={{ fontWeight: 500 }}>{params.value}</span>;
        },
      },
      {
        field: twoYearsAgoField,
        headerName: twoYearsAgoField,
        flex: 1.2,
        type: "number",
        renderCell: renderYearCell(),
      },
      {
        field: previousYearField,
        headerName: previousYearField,
        flex: 1.5,
        type: "number",
        renderCell: renderYearCell(twoYearsAgoField),
      },
      {
        field: currentYearField,
        headerName: currentYearField,
        flex: 1.5,
        type: "number",
        renderCell: renderYearCell(previousYearField),
      },
    ];
  }, [
    gettext,
    twoYearsAgoFilter.fromDate,
    previousYearFilter.fromDate,
    thisYearFilter.fromDate,
    locale,
    theme,
    isDisplayM3,
  ]);

  const initialState = useMemo(
    () => ({
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 100,
        },
      },
    }),
    [],
  );

  const handleGetSelectedValues = (selectedValues: SelectedValuesPayload) => {
    const getOptionValue = (
      option: { value?: string } | string | undefined,
    ) => {
      if (typeof option === "string") {
        return option;
      }

      return option?.value;
    };

    const buildingValue = getOptionValue(selectedValues?.building);
    if (buildingValue) {
      setBuilding(buildingValue);
    }

    const meterValue = getOptionValue(selectedValues?.meter);
    if (meterValue) {
      setMeter(meterValue);
    }

    if (selectedValues?.energyType) {
      const val =
        typeof selectedValues.energyType === "object"
          ? selectedValues.energyType.value ||
            (selectedValues.energyType as any).label
          : selectedValues.energyType;
      if (val) setEnergyType(val);
    }

    if (selectedValues?.date?.startDate) {
      setFromDate(selectedValues.date.startDate.toISOString());
    }

    if (selectedValues?.date?.endDate) {
      setToDate(selectedValues.date.endDate.toISOString());
    }

    if (selectedValues?.segment) {
      setShortSegment(segments[selectedValues.segment].shortSegment);
    }

    if (selectedValues?.country) {
      setCountry(selectedValues.country);
    }

    const unitValue = getOptionValue(selectedValues?.unit);
    if (unitValue) {
      setUnit(unitValue);
    }

    const dayValue = getOptionValue(selectedValues?.day);
    if (dayValue) {
      setDay(dayValue);
    }
  };

  const handleClickCustomDropdownOption = useCallback(
    (obj: { key?: string }) => {
      let renamedGroup = group;
      if (subsidiary.length < 2) {
        renamedGroup = "all";
      }
      switch (obj.key) {
        case "generate-report":
          {
            const isGasMeter =
              energyType === "gas" || energyType === "natural gas";
            const normalizedUnit =
              typeof unit === "string" ? unit.toLowerCase() : "kwh";
            const normalizedDay =
              typeof day === "string" ? day.toLowerCase() : "none";

            addExcelInQueue({
              variables: {
                meterType: energyType,
                customer: customer,
                group: renamedGroup,
                year: new Date(fromDate).getFullYear(),
                isClimate: false,
                ...(isGasMeter
                  ? {
                      isM3: normalizedUnit === "m3",
                      dayReport:
                        normalizedDay === "none"
                          ? undefined
                          : normalizedDay === "day",
                    }
                  : {}),
              },
            }).then(
              ({
                data,
              }: {
                data: { generateEnergyStatementFile?: string };
              }) => {
                if (data?.generateEnergyStatementFile) {
                  enqueueSnackbar(
                    gettext(
                      "The system is now creating the report, and you can download it shortly by clicking Download.",
                    ),
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    gettext(
                      "An error occurred while the system was generating the report. Please try again. You can contact support If this continues.",
                    ),
                    { variant: "error" },
                  );
                }
              },
            );
          }
          break;
        default:
          break;
      }
    },
    [
      group,
      subsidiary.length,
      addExcelInQueue,
      energyType,
      customer,
      fromDate,
      unit,
      day,
      gettext,
    ],
  );

  const postToolbar = useCallback(() => {
    return (
      <ToolbarDropdown
        data={exportData}
        label={gettext("Reports")}
        onClick={handleClickCustomDropdownOption}
        endIcon={<TfiImport size={14} />}
      />
    );
  }, [exportData, gettext, handleClickCustomDropdownOption]);
  //endregion For Table

  return (
    <>
      <FilterBar1
        getSelectedValues={handleGetSelectedValues}
        currentTeam={customer}
        currentSubsidiary={group}
        currentTeamId={currentTeamId}
        currentSubsidiaryId={currentSubsidiaryId}
        showCountry
        showBuilding
        showMeter
        showEnergyType
        showDateSingle
        showDateSingleHourly
        showDateSingleDaily
        showDateSingleMonthly
        // showDateMultiple
        showBuildingAll
        showMeterAll
        showGasUnit
        // showEnergyTypeAll
      />
      <br />

      {showAreaChart && (
        <>
          <AreaChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={
              isDisplayM3
                ? gettext("Consumption (m³)")
                : gettext("Consumption (kWh)")
            }
            chartId={"consumption"}
            data={{
              dataset: chartData as any,
              series: [
                {
                  dataKey: isDisplayM3
                    ? `${new Date(twoYearsAgoFilter.fromDate).getFullYear()}_m3`
                    : new Date(twoYearsAgoFilter.fromDate)
                        .getFullYear()
                        .toString(),
                  label: new Date(twoYearsAgoFilter.fromDate)
                    .getFullYear()
                    .toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: isDisplayM3 ? gettext("m³") : gettext("kWh"),
                },
                {
                  dataKey: isDisplayM3
                    ? `${new Date(previousYearFilter.fromDate).getFullYear()}_m3`
                    : new Date(previousYearFilter.fromDate)
                        .getFullYear()
                        .toString(),
                  label: new Date(previousYearFilter.fromDate)
                    .getFullYear()
                    .toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: isDisplayM3 ? gettext("m³") : gettext("kWh"),
                },
                {
                  dataKey: isDisplayM3
                    ? `${new Date(thisYearFilter.fromDate).getFullYear()}_m3`
                    : new Date(thisYearFilter.fromDate)
                        .getFullYear()
                        .toString(),
                  label: new Date(thisYearFilter.fromDate)
                    .getFullYear()
                    .toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: isDisplayM3 ? gettext("m³") : gettext("kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {showLineChart && (
        <>
          <LineChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={
              isDisplayM3
                ? gettext("Consumption (m³)")
                : gettext("Consumption (kWh)")
            }
            chartId={"consumption-line"}
            data={{
              dataset: chartData as any,
              series: [
                {
                  dataKey: isDisplayM3
                    ? `${new Date(twoYearsAgoFilter.fromDate).getFullYear()}_m3`
                    : new Date(twoYearsAgoFilter.fromDate)
                        .getFullYear()
                        .toString(),
                  label: new Date(twoYearsAgoFilter.fromDate)
                    .getFullYear()
                    .toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: isDisplayM3 ? gettext("m³") : gettext("kWh"),
                },
                {
                  dataKey: isDisplayM3
                    ? `${new Date(previousYearFilter.fromDate).getFullYear()}_m3`
                    : new Date(previousYearFilter.fromDate)
                        .getFullYear()
                        .toString(),
                  label: new Date(previousYearFilter.fromDate)
                    .getFullYear()
                    .toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: isDisplayM3 ? gettext("m³") : gettext("kWh"),
                },
                {
                  dataKey: isDisplayM3
                    ? `${new Date(thisYearFilter.fromDate).getFullYear()}_m3`
                    : new Date(thisYearFilter.fromDate)
                        .getFullYear()
                        .toString(),
                  label: new Date(thisYearFilter.fromDate)
                    .getFullYear()
                    .toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: isDisplayM3 ? gettext("m³") : gettext("kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {showBarChart && (
        <>
          <BarChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={
              isDisplayM3
                ? gettext("Consumption (m³)")
                : gettext("Consumption (kWh)")
            }
            chartId="consumption-bar"
            data={{
              dataset: chartData as any,
              series: [
                {
                  dataKey: isDisplayM3
                    ? `${new Date(twoYearsAgoFilter.fromDate).getFullYear()}_m3`
                    : new Date(twoYearsAgoFilter.fromDate)
                        .getFullYear()
                        .toString(),
                  label: new Date(twoYearsAgoFilter.fromDate)
                    .getFullYear()
                    .toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: isDisplayM3 ? gettext("m³") : gettext("kWh"),
                },
                {
                  dataKey: isDisplayM3
                    ? `${new Date(previousYearFilter.fromDate).getFullYear()}_m3`
                    : new Date(previousYearFilter.fromDate)
                        .getFullYear()
                        .toString(),
                  label: new Date(previousYearFilter.fromDate)
                    .getFullYear()
                    .toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: isDisplayM3 ? gettext("m³") : gettext("kWh"),
                },
                {
                  dataKey: isDisplayM3
                    ? `${new Date(thisYearFilter.fromDate).getFullYear()}_m3`
                    : new Date(thisYearFilter.fromDate)
                        .getFullYear()
                        .toString(),
                  label: new Date(thisYearFilter.fromDate)
                    .getFullYear()
                    .toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: isDisplayM3 ? gettext("m³") : gettext("kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      <TableView1
        title={
          isDisplayM3
            ? gettext("Consumption (m³)")
            : gettext("Consumption (kWh)")
        }
        rows={tableData}
        columns={columns}
        getRowHeight={() => "auto"}
        hideFooterPagination
        loading={loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo}
        initialState={initialState}
        postToolbar={postToolbar()}
        showGridToolbar
        // showGridToolbarCustomExport
        disableColumnSorting
        disableColumnSelector
        // exportData={exportData}
        getRowClassName={(params: GridRowClassNameParams) => {
          return params.row.id === "Total" ? "total-row" : "";
        }}
      />
    </>
  );
};
